import { Injectable, signal } from '@angular/core';

export type ClipboardFeedback = {
    status: 'success' | 'error';
    message: string;
};

@Injectable({ providedIn: 'root' })
export class ClipboardService {
    private readonly feedbackSignal = signal<ClipboardFeedback | null>(null);
    private dismissTimer: ReturnType<typeof setTimeout> | undefined;

    readonly feedback = this.feedbackSignal.asReadonly();

    async copy(value: string, successMessage = 'Copied to clipboard.'): Promise<boolean> {
        try {
            const clipboard = typeof navigator === 'undefined'
                ? undefined
                : navigator.clipboard;

            if (!clipboard?.writeText) {
                throw new Error('Clipboard access is unavailable.');
            }

            await clipboard.writeText(value);
            this.showFeedback('success', successMessage);
            return true;
        } catch {
            this.showFeedback('error', 'Could not copy to clipboard. Please try again.');
            return false;
        }
    }

    dismiss() {
        if (this.dismissTimer) {
            clearTimeout(this.dismissTimer);
            this.dismissTimer = undefined;
        }

        this.feedbackSignal.set(null);
    }

    private showFeedback(status: ClipboardFeedback['status'], message: string) {
        this.dismiss();
        this.feedbackSignal.set({ status, message });
        this.dismissTimer = setTimeout(() => this.dismiss(), 3000);
    }
}
